import service from './request';
import { cachedRequest } from './cache';
import { withPreload } from './preload';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SolutionFeature {
  icon: string;
  name: string;
  desc: string;
}

export interface SubSolution {
  id: string;
  name: string;
  desc: string;
  image: string;
  features: SolutionFeature[];
  scenarios: string[];
  benefits: { value: string; label: string }[];
  /** 子方案排序值（同分类内升序），由后端 biz_solution.sort 透传，接口已按该字段升序返回 */
  sort: number;
}

export interface SolutionCategory {
  id: string;
  icon: string;
  tag: string;
  title: string;
  subtitle: string;
  desc: string;
  image: string;
  features: SolutionFeature[];
  subSolutions: SubSolution[];
  /** 分类排序值（Active_Category_Set 升序），由后端 biz_front_solution_category.sort 透传 */
  sort: number;
}

export interface SubScene {
  id: string;
  icon: string;
  name: string;
  desc: string;
  tags: string[];
  productCategory: string;
  image: string;
  parentCategory: string;
  parentName: string;
  accentColor: string;
  heroImage: string;
  introduction: string;
  introTags?: string[];
  introImage?: string;
  painPointsImage?: string;
  painPoints?: { icon: string; title: string; desc: string }[];
  features: { icon: string; title: string; desc: string }[];
  benefits: { icon: string; title: string; value: string; desc: string }[];
  relatedCaseSolution: string;
}

// ─── API calls ────────────────────────────────────────────────────────────────

/**
 * 把 `GET /api/front/solutions` 的原始响应规范化为前台约定的形状。
 *
 * 后端 `BizFrontSolutionCategory` 的 `id` 是数据库自增主键（如 1、2、104），
 * 分类标识（`info-security` / `cloud-xinda` 等）在 `categoryId` 字段。
 * 前台的 Tab、Hero、主题色、SEO 文案、案例映射都以分类标识为键，
 * 因此在数据入口统一把 `id` 置为分类标识，调用方不必再各自 `categoryId || id`。
 * 该函数幂等：`categoryId` 缺失时保留原 `id`（例如 mock 数据已是标识）。
 */
export function normalizeSolutionTree(raw: unknown): SolutionCategory[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((cat: any) => {
    const slug = typeof cat?.categoryId === 'string' && cat.categoryId ? cat.categoryId : cat?.id;
    return { ...cat, id: String(slug) } as SolutionCategory;
  });
}

export function getSolutionTree(): Promise<SolutionCategory[]> {
  // 在出口规范化：首页聚合接口与预渲染内嵌状态会直接向 'solutions/tree' 缓存注入原始响应，
  // 只有出口处理才能覆盖所有来源；函数幂等。
  return cachedRequest('solutions/tree', () => service.get('/api/front/solutions')).then((res: unknown) => normalizeSolutionTree(res));
}

export function getSolutionDetail(id: string): Promise<any> {
  return service.get('/api/front/solutions/' + id);
}

// 直连调用（不走 cachedRequest），接入 preload 适配器：首屏命中内嵌状态直接返回
// 并标记消耗，后续交互（同一 sceneId 再次调用）走真实请求。
export const getSceneDetail = withPreload(
  'GET',
  (sceneId: string) => '/api/front/solutions/scenes/' + sceneId,
  (sceneId: string): Promise<SubScene> => service.get('/api/front/solutions/scenes/' + sceneId),
);

// 分类核心优势（为什么选择我们）
export function getCategoryAdvantage(category: string): Promise<any> {
  return service.get('/api/front/solutions/advantages/' + category);
}
